import { AxiosResponse } from "axios";

import { ROUTES } from "@/constants/routes";
import type {
  FertilityOrderingEligibility,
  ProviderStockOrderPricing,
  StockProductId,
} from "@/pages/providers/orderV2Types";
import { ProviderTestOrder } from "@/types/provider";
import { getRequest, postRequest } from "@/utils/axios";

export type ProviderTestOrdersQuery = {
  search?: string;
  limit?: number;
  offset?: number;
};

const getProviderTestOrders = (
  query: ProviderTestOrdersQuery = {}
): Promise<AxiosResponse<ProviderTestOrder[]>> => {
  const queryParams = new URLSearchParams();
  if (query.search) queryParams.set("search", query.search);
  if (query.limit !== undefined) {
    queryParams.set("limit", query.limit.toString());
  }
  if (query.offset !== undefined) {
    queryParams.set("offset", query.offset.toString());
  }
  const queryString = queryParams.toString();
  const response = getRequest(
    `/api/v1/provider-test-orders/${queryString ? `?${queryString}` : ""}`,
    (response: any) => {
      return response;
    },
    (error: any, response: any) => {
      throw error;
    }
  );
  return response;
};

export type ProviderBulkCheckoutShippingAddress = {
  address1: string;
  address2?: string;
  city: string;
  countryCode: "US" | "CA";
  firstName: string;
  lastName: string;
  phone?: string;
  provinceCode: string;
  zip: string;
};

export type CreateProviderBulkTestOrderData =
  | {
      num_ordered: number;
      add_sti: boolean;
    }
  | {
      line_items: ProviderStockOrderLineItem[];
      shipping_address?: ProviderBulkCheckoutShippingAddress;
    };

export type CreateProviderBulkTestOrderResponse = {
  checkout_url: string;
};

export type ProviderStockOrderLineItem = {
  product: StockProductId;
  quantity: number;
};

export type CreateProviderDirectShipOrderData = {
  patient_email: string;
  payer: "patient" | "provider";
  line_items: ProviderStockOrderLineItem[];
};

export type CreateProviderDirectShipOrderResponse = {
  provider_order_group_id: string;
  provider_test_order_ids: number[];
  num_ordered: number;
  checkout_url?: string;
  checkout_delivery_status?: "pending" | "in-progress" | "sent" | "failed";
};

const createProviderDirectShipOrder: (
  data: CreateProviderDirectShipOrderData,
  idempotencyKey: string
) => Promise<AxiosResponse<CreateProviderDirectShipOrderResponse>> = (
  data,
  idempotencyKey
) => {
  const response = postRequest(
    "/api/v1/provider-test-orders/direct-ship-create/",
    data,
    (response: any) => response,
    (error: any) => {
      throw error;
    },
    { headers: { "Idempotency-Key": idempotencyKey } }
  );
  return response;
};

export type CreateProviderStockOrderData = {
  snbl_payer: "provider" | "patient";
  line_items: ProviderStockOrderLineItem[];
  shipping_address: ProviderBulkCheckoutShippingAddress;
};

export type CreateProviderStockOrderResponse = {
  bulk_order_id: string;
  provider_test_order_ids: number[];
  num_ordered: number;
  snbl_payer: "provider" | "patient";
  snbl_payment_status: string;
  line_items: Array<
    ProviderStockOrderLineItem & {
      add_expanded_pcr: boolean;
      provider_test_order_ids: number[];
    }
  >;
};

export type PendingProviderStock = {
  order_id: number;
  order_number: string | null;
  product: ProviderTestOrder["product"];
  quantity: number;
  status: "preparing" | "in_transit" | "awaiting_kit_details";
  create_date: string;
};

export type AssignableProviderStockKit = {
  product?: ProviderTestOrder["product"];
  provider_test_order_id: number;
  test_hash: string;
  test_pretty_hash: string;
  test_type: string;
  add_expanded_pcr: boolean;
  snbl_payer: "provider" | "patient" | null;
  create_date: string;
};

export type AssignProviderStockKitData = {
  test_hash: string;
  patient_email: string;
  patient_name?: string;
  collection_location: "patient-home" | "clinic";
  consent_confirmed: boolean;
};

const getAssignableProviderStockKits = () =>
  getRequest(
    "/api/v1/provider-test-orders/assignable-stock/",
    (response: any) => response,
    (error: any) => {
      throw error;
    }
  ) as Promise<AxiosResponse<AssignableProviderStockKit[]>>;

const getPendingProviderStock = () =>
  getRequest(
    "/api/v1/provider-test-orders/pending-stock/",
    (response: any) => response,
    (error: any) => {
      throw error;
    }
  ) as Promise<AxiosResponse<PendingProviderStock[]>>;

const assignProviderStockKit = (data: AssignProviderStockKitData) =>
  postRequest(
    "/api/v1/provider-test-orders/assign-stock/",
    data,
    (response: any) => response,
    (error: any) => {
      throw error;
    }
  ) as Promise<AxiosResponse<ProviderTestOrder>>;

const createProviderBulkTestOrder: (
  data: CreateProviderBulkTestOrderData
) => Promise<AxiosResponse<CreateProviderBulkTestOrderResponse>> = (data) => {
  const response = postRequest(
    "/api/v1/provider-test-orders/bulk_create/",
    data,
    (response: any) => {
      return response;
    },
    (error: any, response: any) => {
      throw error;
    }
  );
  return response;
};

const createProviderStockOrder: (
  data: CreateProviderStockOrderData,
  idempotencyKey: string
) => Promise<AxiosResponse<CreateProviderStockOrderResponse>> = (
  data,
  idempotencyKey
) => {
  const response = postRequest(
    "/api/v1/provider-test-orders/stock-create/",
    data,
    (response: any) => {
      return response;
    },
    (error: any, response: any) => {
      throw error;
    },
    { headers: { "Idempotency-Key": idempotencyKey } }
  );
  return response;
};

const getProviderStockOrderPricing: () => Promise<
  AxiosResponse<ProviderStockOrderPricing>
> = () => {
  const response = getRequest(
    "/api/v1/provider-test-orders/stock-order-pricing/",
    (response: any) => response,
    (error: any) => {
      throw error;
    }
  );
  return response;
};

const getFertilityOrderingEligibility: () => Promise<
  AxiosResponse<FertilityOrderingEligibility>
> = () => {
  const response = getRequest(
    "/api/v1/provider-test-orders/fertility-ordering-eligibility/",
    (response: any) => response,
    (error: any) => {
      throw error;
    }
  );
  return response;
};

const remindPatient: (patientEmail: string) => Promise<AxiosResponse<void>> = (
  patientEmail
) => {
  const response = postRequest(
    "/api/v1/provider-test-orders/remind_patient/",
    {
      patient_email: patientEmail,
    },
    (response: any) => {
      return response;
    },
    (error: any, response: any) => {
      throw error;
    }
  );
  return response;
};

function fetchPlanItems(providerTestOrderId: number) {
  return getRequest(
    `/api/v1/provider-test-orders/${providerTestOrderId}/plan-items`,
    (response: any) => {
      return response.data;
    },
    (error: any) => {
      return error;
    }
  );
}

export type ProviderMagicLinkCountry = "US" | "CA";
export type ProviderMagicLinkProduct = "vht" | "expan" | "fertility";

// Provider magic link endpoints
const getProviderMagicLink: () => Promise<
  AxiosResponse<{ slug: string }>
> = () => {
  // Server generates slug on-demand if missing
  const response = getRequest(
    "/api/v1/provider-test-orders/magic-link/",
    (response: any) => response,
    (error: any) => {
      throw error;
    }
  );
  return response;
};

const fetchProviderMagicLinkConfig: (token: string) => Promise<
  AxiosResponse<{
    country: ProviderMagicLinkCountry;
    fertility: {
      enabled: boolean;
      direct_shipping_patient_configured: boolean;
      price_cents: number | null;
      discounted_price_cents: number | null;
    };
  }>
> = (token) => {
  const response = getRequest(
    `${ROUTES.PROVIDER_TEST_API}${encodeURIComponent(token)}/config/`,
    (response: any) => response,
    (error: any) => {
      throw error;
    }
  );
  return response;
};

const fetchPatientPayCheckoutURL: (
  token: string,
  countryCode?: ProviderMagicLinkCountry,
  product?: ProviderMagicLinkProduct
) => Promise<AxiosResponse<{ checkout_url: string }>> = (
  token,
  countryCode,
  product
) => {
  // Exchange the picker selection for a Shopify checkout URL. The provider slug
  // remains in the API request so the backend can stamp provider_magic on the cart.
  const queryParams = new URLSearchParams();
  if (countryCode) queryParams.set("country", countryCode);
  if (product) queryParams.set("product", product);

  const query = queryParams.toString();
  const url = `${ROUTES.PROVIDER_TEST_API}${encodeURIComponent(token)}/${
    query ? `?${query}` : ""
  }`;
  const response = getRequest(
    url,
    (response: any) => response,
    (error: any) => {
      throw error;
    }
  );
  return response;
};

const markResultDownloaded: (
  providerTestOrderId: number
) => Promise<AxiosResponse<void>> = (providerTestOrderId) => {
  const response = postRequest(
    `/api/v1/provider-test-orders/${providerTestOrderId}/mark-downloaded/`,
    {},
    (response: any) => response,
    (error: any) => {
      throw error;
    }
  );
  return response;
};

export const providerService = {
  createProviderDirectShipOrder,
  createProviderBulkTestOrder,
  createProviderStockOrder,
  getFertilityOrderingEligibility,
  getProviderStockOrderPricing,
  getAssignableProviderStockKits,
  getPendingProviderStock,
  assignProviderStockKit,
  getProviderTestOrders,
  remindPatient,
  fetchPlanItems,
  getProviderMagicLink,
  fetchProviderMagicLinkConfig,
  fetchPatientPayCheckoutURL,
  markResultDownloaded,
};
